import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import {
  Pill, TestTube, History, Calendar, Loader2, Check, X, Sparkles,
  Stethoscope, Clock3, ClipboardCheck, ArrowRight, FileText,
  Activity, Microscope, NotebookPen, MessageSquare, Layers,
} from 'lucide-react';
import { formatDateIST } from '../utils/date';
import { formatDoctorName } from '../utils/doctor';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Narrative sections the doctor can carry forward. Order drives display.
// Each row in the modal toggles whether THAT section is copied on Apply.
// `defaultChecked` controls the initial state — diagnoses ON, others OFF.
const NARRATIVE_SECTIONS = [
  { key: 'final_diagnosis', label: 'Final Diagnosis', icon: ClipboardCheck, defaultChecked: true, accent: 'amber' },
  { key: 'provisional_diagnosis', label: 'Provisional Diagnosis', icon: ClipboardCheck, defaultChecked: true, accent: 'amber' },
  { key: 'treatment_plan', label: 'Treatment Plan', icon: NotebookPen, defaultChecked: true, accent: 'emerald' },
  { key: 'chief_complaints', label: 'Chief Complaints', icon: MessageSquare, defaultChecked: false, accent: 'slate' },
  { key: 'past_history', label: 'Past History', icon: History, defaultChecked: false, accent: 'slate' },
  { key: 'clinical_examination', label: 'Clinical Examination', icon: Activity, defaultChecked: false, accent: 'slate' },
  { key: 'tumor_board', label: 'Tumor Board', icon: Microscope, defaultChecked: false, accent: 'rose' },
  { key: 'visit_notes', label: 'Visit Notes', icon: NotebookPen, defaultChecked: false, accent: 'slate' },
];

// "Impressive & informative" prefill modal — ALWAYS shows when a prior
// visit exists. Doctor picks per-section what to carry forward; Apply
// MERGES the picked sections (rx/tests dedupe by name, narratives copy
// content). This replaces the previous "auto-suppress if has items" guard.
export default function PreviousVisitPrefillModal({
  open,
  onClose,
  consultationId,
  previousVisit,
  currentConsultation,
  onApplied,
}) {
  const [loading, setLoading] = useState(false);

  // Per-section selection state. Keyed by section name; value=bool.
  // Defaults: prescription=true (medicines), investigations=true (diagnostics),
  // final/provisional diagnosis=true; rest=false.
  const [sel, setSel] = useState(() => ({
    prescription: true,
    investigations: true,
    final_diagnosis: true,
    provisional_diagnosis: true,
    chief_complaints: false,
    past_history: false,
    clinical_examination: false,
    tumor_board: false,
    treatment_plan: false,
    visit_notes: false,
  }));

  const meds = previousVisit?.prescription || [];
  const tests = previousVisit?.investigations || [];
  const narratives = previousVisit?.narratives || {};

  // Reset checkboxes whenever the modal opens for a new visit (so a
  // previously-dismissed selection doesn't bleed into the next patient).
  useEffect(() => {
    if (open && previousVisit) {
      setSel({
        prescription: meds.length > 0,
        investigations: tests.length > 0,
        final_diagnosis: !!narratives.final_diagnosis?.has_content,
        provisional_diagnosis: !!narratives.provisional_diagnosis?.has_content,
        treatment_plan: !!narratives.treatment_plan?.has_content,
        chief_complaints: false,
        past_history: false,
        clinical_examination: false,
        tumor_board: false,
        visit_notes: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, previousVisit?.id]);

  const daysAgo = useMemo(() => {
    if (!previousVisit?.created_at) return null;
    try {
      const diffMs = Date.now() - new Date(previousVisit.created_at).getTime();
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    } catch { return null; }
  }, [previousVisit]);

  // Count what's selected — drives the footer summary and disables Apply
  // when nothing is picked. Empty narrative sections never count toward the
  // total even if the checkbox state is true (defensive against stale state).
  const selectionSummary = useMemo(() => {
    const parts = [];
    if (sel.prescription && meds.length) parts.push(`${meds.length} medicine${meds.length !== 1 ? 's' : ''}`);
    if (sel.investigations && tests.length) parts.push(`${tests.length} test${tests.length !== 1 ? 's' : ''}`);
    const narrCount = NARRATIVE_SECTIONS.filter(s => sel[s.key] && narratives[s.key]?.has_content).length;
    if (narrCount) parts.push(`${narrCount} narrative section${narrCount !== 1 ? 's' : ''}`);
    return parts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, meds.length, tests.length, narratives]);

  // Pick a sensible default tab when the previous visit has no items.
  // Must be declared BEFORE the conditional return so React hook order is stable.
  const hasItems = meds.length > 0 || tests.length > 0;
  // Always show the default-checked narrative sections in the picker so the
  // doctor can see (and override) the default policy regardless of what the
  // prior visit happened to contain. Other sections appear only when prior
  // visit actually had content to copy from.
  const visibleNarratives = NARRATIVE_SECTIONS.filter(
    (s) => s.defaultChecked || narratives[s.key]?.has_content
  );
  const hasNarratives = visibleNarratives.length > 0;

  // Lock body scroll & wire ESC while drawer is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!previousVisit) return null;

  const applyPrefill = async () => {
    setLoading(true);
    try {
      const calls = [];
      const currentSections = currentConsultation?.sections || {};

      // Prescription — MERGE: keep current items, append previous ones
      // whose medicine_name isn't already in the list (case-insensitive).
      // Tag each appended row with `from_previous_visit` so the
      // consultation-level "carry-forward banner" can surface a one-click
      // strip when the doctor wants a clean slate.
      if (sel.prescription && meds.length > 0) {
        const currentRx = ((currentSections.prescription || {}).items) || [];
        const have = new Set(currentRx.map(m => (m.medicine_name || '').trim().toLowerCase()).filter(Boolean));
        const toAdd = meds
          .filter(m => !have.has((m.medicine_name || '').trim().toLowerCase()))
          .map((m) => ({ ...m, from_previous_visit: true }));
        const merged = [...currentRx, ...toAdd];
        calls.push(
          axios.post(`${API_URL}/api/consultations/save-draft`, {
            encounter_id: consultationId,
            section: 'prescription',
            content: merged,
          }, { withCredentials: true })
        );
      }

      // Investigations — MERGE: dedupe by test name. Coerce to plain
      // strings so the saved shape stays clean (legacy docs may carry
      // `{name: 'CBC'}` objects which crash render with React #31).
      if (sel.investigations && tests.length > 0) {
        const currentInv = ((currentSections.investigations || {}).items) || [];
        const nameOf = (t) => (typeof t === 'string' ? t : (t.name || t.test_name || '')).trim();
        const have = new Set(currentInv.map(nameOf).filter(Boolean).map((s) => s.toLowerCase()));
        const cleanCurrent = currentInv.map(nameOf).filter(Boolean);
        const additions = tests
          .map(nameOf)
          .filter((s) => s && !have.has(s.toLowerCase()));
        const merged = [...cleanCurrent, ...additions];
        calls.push(
          axios.post(`${API_URL}/api/consultations/save-draft`, {
            encounter_id: consultationId,
            section: 'investigations',
            content: merged,
          }, { withCredentials: true })
        );
      }

      // Narrative sections — overwrite ONLY when previous has content AND
      // the box is checked. Doctor's existing narrative stays untouched
      // for sections they did NOT tick. Skip the append if the previous
      // content is already embedded in the current — guards against
      // accidental double-applies that bloat the document and can later
      // cause TipTap render crashes on enormous strings.
      for (const s of NARRATIVE_SECTIONS) {
        if (!sel[s.key]) continue;
        const prevHtml = narratives[s.key]?.content || '';
        if (!prevHtml) continue;
        const currentHtml = (currentSections[s.key] || {}).content || '';
        const stripTags = (html) => String(html || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
        const prevPlain = stripTags(prevHtml);
        const currentPlain = stripTags(currentHtml);
        // Idempotency check: if the previous visit's plain-text is already
        // present in the current section, do nothing for this section.
        if (prevPlain && currentPlain.includes(prevPlain)) {
          continue;
        }
        const next = currentHtml
          ? `${currentHtml}<hr/><p><em>Carried forward from previous visit:</em></p>${prevHtml}`
          : prevHtml;
        calls.push(
          axios.post(`${API_URL}/api/consultations/save-draft`, {
            encounter_id: consultationId,
            section: s.key,
            content: next,
          }, { withCredentials: true })
        );
      }

      if (calls.length === 0) {
        toast.info('Nothing to load — pick at least one section.');
        return;
      }
      await Promise.all(calls);
      toast.success(`Carried forward ${calls.length} section${calls.length !== 1 ? 's' : ''} from previous visit.`);
      onApplied?.();
      onClose();
    } catch (e) {
      console.error('Prefill error:', e);
      toast.error('Could not carry forward. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const visitDate = previousVisit.created_at ? formatDateIST(previousVisit.created_at) : '—';

  return (
    <div
      className={`fixed inset-0 z-[60] ${open ? '' : 'pointer-events-none opacity-0'}`}
      data-testid="prev-visit-prefill-modal"
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 bottom-0 w-full max-w-3xl bg-white shadow-2xl flex flex-col transition-transform duration-200 ${open ? 'translate-x-0' : 'translate-x-full'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ───────── HERO (preserved bespoke design) ───────── */}
        <div className="relative bg-gradient-to-br from-indigo-700 via-blue-700 to-cyan-600 text-white overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 opacity-[0.08]"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
          <div className="absolute -top-12 -right-12 w-56 h-56 bg-cyan-300/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-8 -left-10 w-48 h-48 bg-indigo-400/20 rounded-full blur-3xl" />
          <div className="relative px-7 pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <Badge className="bg-white/15 text-white border-white/20 backdrop-blur-sm gap-1.5">
                <Sparkles className="h-3 w-3" /> Smart Carry-Forward
              </Badge>
              <button onClick={onClose} className="text-white/70 hover:text-white hover:bg-white/10 transition rounded-md p-1"
                data-testid="prev-visit-close-x" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-start gap-3.5">
              <div className="h-12 w-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center flex-shrink-0">
                <History className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }}>
                  Previous visit found
                </h2>
                <p className="text-[13px] text-white/75 mt-0.5">
                  Pick the sections to carry forward. Items merge, narratives append below your edits.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4">
              <KPI icon={Calendar} label="Visit date" value={visitDate} />
              <KPI icon={Clock3} label="Time since" value={daysAgo !== null ? `${daysAgo}d ago` : '—'} />
              <KPI icon={Pill} label="Medicines" value={meds.length} accent={meds.length > 0} />
              <KPI icon={TestTube} label="Tests" value={tests.length} accent={tests.length > 0} />
            </div>
            {previousVisit.doctor_name && (
              <div className="flex items-center gap-2 mt-4 text-xs text-white/70">
                <Stethoscope className="h-3.5 w-3.5" />
                <span>Reviewed by <b className="text-white">{formatDoctorName(previousVisit.doctor_name)}</b></span>
                {previousVisit.appointment_id && (
                  <>
                    <span>·</span>
                    <span className="font-mono text-[10px] bg-white/10 px-1.5 py-0.5 rounded">{previousVisit.appointment_id}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ───────── DIAGNOSIS STRIP ───────── */}
        {previousVisit.diagnosis_snippet && (
          <div className="px-7 py-3 bg-amber-50 border-b border-amber-100" data-testid="prev-visit-diagnosis-snippet">
            <div className="flex items-start gap-2.5">
              <div className="h-7 w-7 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                <ClipboardCheck className="h-3.5 w-3.5 text-amber-700" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Diagnosis last time</p>
                <p className="text-sm text-amber-950 leading-snug line-clamp-2">{previousVisit.diagnosis_snippet}</p>
              </div>
            </div>
          </div>
        )}

        {/* ───────── SINGLE UNIFIED SECTIONS LIST ───────── */}
        <div className="px-7 pt-4 pb-2 bg-white flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
            <NotebookPen className="h-4 w-4 text-indigo-600" />
            <span>Sections</span>
            <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-indigo-50 text-indigo-700 border-indigo-200">
              {(meds.length > 0 ? 1 : 0) + (tests.length > 0 ? 1 : 0) + visibleNarratives.length}
            </Badge>
          </div>
          <button
            onClick={() => {
              // Toggle ALL rows at once. Drives a quick "select everything
              // that has data" workflow. Empty narrative rows stay off.
              const allOn =
                (meds.length === 0 || sel.prescription) &&
                (tests.length === 0 || sel.investigations) &&
                visibleNarratives.every(s => !narratives[s.key]?.has_content || sel[s.key]);
              setSel((prev) => {
                const next = { ...prev };
                next.prescription = !allOn && meds.length > 0;
                next.investigations = !allOn && tests.length > 0;
                for (const s of visibleNarratives) {
                  next[s.key] = !allOn && !!narratives[s.key]?.has_content;
                }
                return next;
              });
            }}
            data-testid="prev-visit-select-all"
            className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
          >
            <Layers className="h-3 w-3" /> Toggle all
          </button>
        </div>

        {/* ───────── BODY ───────── */}
        <ScrollArea className="flex-1 bg-slate-50/40">
          <div className="px-7 py-3 space-y-2">
            {!hasItems && !hasNarratives && (
              <div className="text-center py-10" data-testid="prev-visit-empty-body">
                <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500 italic">
                  The previous visit has no copyable content.
                </p>
              </div>
            )}

            {/* Medicines row */}
            {meds.length > 0 && (
              <ItemSummaryRow
                checked={sel.prescription}
                onChange={(v) => setSel((p) => ({ ...p, prescription: v }))}
                icon={Pill}
                accent="blue"
                label="Medicines"
                count={meds.length}
                preview={meds
                  .map((m) => m.medicine_name)
                  .filter(Boolean)
                  .slice(0, 4)
                  .join(' · ') + (meds.length > 4 ? ` · +${meds.length - 4} more` : '')}
                hint="Merges with current prescription — duplicates skipped"
                testId="prev-visit-toggle-prescription"
              />
            )}

            {/* Diagnostics / Investigations row */}
            {tests.length > 0 && (
              <ItemSummaryRow
                checked={sel.investigations}
                onChange={(v) => setSel((p) => ({ ...p, investigations: v }))}
                icon={TestTube}
                accent="purple"
                label="Diagnostics"
                count={tests.length}
                preview={tests
                  .map((t) => (typeof t === 'string' ? t : (t.name || t.test_name || '')))
                  .filter(Boolean)
                  .slice(0, 4)
                  .join(' · ') + (tests.length > 4 ? ` · +${tests.length - 4} more` : '')}
                hint="Merges with current investigations — duplicates skipped"
                testId="prev-visit-toggle-investigations"
              />
            )}

            {/* Narrative sections */}
            {visibleNarratives.map((s) => (
              <NarrativeRow
                key={s.key}
                section={s}
                bundle={narratives[s.key]}
                checked={!!sel[s.key]}
                onChange={(v) => setSel((p) => ({ ...p, [s.key]: v }))}
              />
            ))}
          </div>
        </ScrollArea>

        {/* ───────── FOOTER ───────── */}
        <div className="px-7 py-4 border-t bg-white flex items-center justify-between gap-3 flex-shrink-0">
          <Button variant="ghost" onClick={onClose} disabled={loading}
            className="text-slate-600 gap-1.5" data-testid="prev-visit-skip-btn">
            <X className="h-4 w-4" /> Start fresh
          </Button>
          <div className="flex items-center gap-3 flex-1 justify-end">
            <span className="hidden sm:inline text-xs text-slate-500" data-testid="prev-visit-selection-summary">
              {selectionSummary.length > 0 ? `Will copy: ${selectionSummary.join(' + ')}` : 'Nothing selected'}
            </span>
            <Button
              onClick={applyPrefill}
              disabled={loading || selectionSummary.length === 0}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-md gap-1.5 px-5"
              data-testid="prev-visit-apply-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Copy Selected
              {!loading && <ArrowRight className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ───────── Inner pieces ─────────

function KPI({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-lg border backdrop-blur-sm px-2.5 py-2 ${
      accent ? 'bg-white/15 border-white/30' : 'bg-white/5 border-white/10'}`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="h-3 w-3 text-white/70" />
        <span className="text-[9px] uppercase tracking-wider text-white/60 font-bold">{label}</span>
      </div>
      <p className="text-base font-bold text-white truncate">{value}</p>
    </div>
  );
}

function ItemSummaryRow({ checked, onChange, icon: Icon, accent, label, count, preview, hint, testId }) {
  // Item-style row (Medicines / Diagnostics). Looks visually consistent with
  // NarrativeRow so the unified Sections list reads as one cohesive picker.
  const accentMap = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  };
  const cls = accentMap[accent] || accentMap.blue;
  return (
    <div
      className={`bg-white border rounded-lg p-3 shadow-sm transition ${
        checked ? 'border-indigo-300' : 'border-slate-200'
      }`}
      data-testid={`prev-visit-item-row-${testId}`}
    >
      <label className="flex items-start gap-2.5 cursor-pointer">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onChange(v === true)}
          className="mt-1"
          data-testid={testId}
        />
        <div className={`h-8 w-8 rounded-md ${cls.bg} ${cls.text} flex items-center justify-center flex-shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="text-sm font-bold text-slate-900">{label}</p>
              <Badge variant="outline" className={`text-[9px] h-4 px-1.5 ${cls.bg} ${cls.text} ${cls.border}`}>
                {count}
              </Badge>
            </div>
            {checked ? (
              <Badge variant="outline" className="text-[9px] gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold">
                <Check className="h-2.5 w-2.5" /> Will copy
              </Badge>
            ) : (
              <span className="text-[10px] text-slate-400 italic">Skipped</span>
            )}
          </div>
          {preview && (
            <p className={`text-xs mt-1 line-clamp-2 ${checked ? 'text-slate-700' : 'text-slate-400'}`}>
              {preview}
            </p>
          )}
          {hint && (
            <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>
          )}
        </div>
      </label>
    </div>
  );
}

function NarrativeRow({ section, bundle, checked, onChange }) {
  const Icon = section.icon;
  const accentMap = {
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' },
  };
  const cls = accentMap[section.accent] || accentMap.slate;
  // Disable the checkbox when the prior visit has no content for this
  // section. Doctor still sees the row (defaults transparency) but can't
  // accidentally tick it on — there's nothing to copy.
  const empty = !bundle?.has_content;
  const effectivelyChecked = !empty && checked;
  return (
    <div
      className={`bg-white border rounded-lg p-3 shadow-sm transition ${
        effectivelyChecked ? 'border-indigo-300' : 'border-slate-200'
      } ${empty ? 'opacity-60' : ''}`}
      data-testid={`prev-visit-narr-row-${section.key}`}
    >
      <label className={`flex items-start gap-2.5 ${empty ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <Checkbox
          checked={effectivelyChecked}
          disabled={empty}
          onCheckedChange={(v) => !empty && onChange(v === true)}
          className="mt-1"
          data-testid={`prev-visit-narr-check-${section.key}`}
        />
        <div className={`h-8 w-8 rounded-md ${cls.bg} ${cls.text} flex items-center justify-center flex-shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-slate-900">{section.label}</p>
            {empty ? (
              <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-400 border-slate-200 italic font-medium">
                Empty in prior visit
              </Badge>
            ) : effectivelyChecked ? (
              <Badge variant="outline" className="text-[9px] gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold">
                <Check className="h-2.5 w-2.5" /> Will copy
              </Badge>
            ) : (
              <span className="text-[10px] text-slate-400 italic">Skipped</span>
            )}
          </div>
          {bundle?.preview ? (
            <p className={`text-xs mt-1 line-clamp-2 ${effectivelyChecked ? 'text-slate-700' : 'text-slate-400'}`}>
              {bundle.preview}
            </p>
          ) : (
            <p className="text-[11px] mt-1 italic text-slate-400">
              No content recorded in the previous visit.
            </p>
          )}
        </div>
      </label>
    </div>
  );
}
